/**
 * AutoCAD point-filter / temporary-tracking token grammar (ТЗ §8.6 object
 * snap / point construction + §10.4 drafting aids).
 *
 * This module is the single source of truth for RECOGNISING the point-filter
 * tokens the typed coordinate grammar accepts and for the pure arithmetic
 * that composes their result:
 *
 *   - `FROM`         — temporary reference: a captured base point, then an
 *                      `@dx,dy` (or absolute/polar) offset → base+offset.
 *   - `M2P` / `MTP`  — "mid between 2 points": two picked/typed points →
 *                      their midpoint `((p1+p2)/2)`.
 *   - `.X`/`.Y`/`.Z` — single-ordinate coordinate filters (plus the
 *   `.XY`/`.XZ`/`.YZ`  two-ordinate combinations): supply one/two ordinates
 *                      from one point, then supply the remaining ordinates
 *                      from a second point.
 *
 * Kept dependency-free (imports nothing from `coordinateInput.ts`) so the
 * package's `coordinateInput → pointFilterTokens` edge stays one-way — the
 * monorepo-wide zero-cycle depcruise gate forbids a back-edge. The
 * `FilterCoordinate` shape is structurally identical to
 * `CoordinatePoint`, so the two interoperate without an import.
 *
 * Reference: Autodesk "Enter Coordinate Filters" / "Track to Points not on
 * an Object" + the `FROM` and `M2P`/`MTP` object-snap keywords; ezdxf has no
 * group-code surface for these (pure input-resolution, no DXF entity).
 */

export interface FilterCoordinate {
  x: number;
  y: number;
  z: number;
}

/**
 * Which ordinates a coordinate filter (`.X`/`.Y`/`.Z`/`.XY`/…) SUPPLIES from
 * its first point. The complement is collected from the follow-up point.
 */
export interface OrdinateFilterAxes {
  x: boolean;
  y: boolean;
  z: boolean;
}

export type PointFilterClassification =
  | { kind: "from" }
  | { kind: "mid" }
  | { kind: "ordinate"; axes: OrdinateFilterAxes };

const AXIS_LETTERS: ReadonlySet<string> = new Set(["X", "Y", "Z"]);

/**
 * Classify a raw point-input token as one of the point-filter /
 * temporary-tracking starters, or `null` when it is an ordinary coordinate
 * token (absolute / relative / polar / direct-distance).
 *
 * Recognises the language-independent `_` prefix (`_from`) and the
 * transparent-command `'` prefix that AutoCAD strips before dispatch.
 * Matching is case-insensitive.
 */
export function classifyPointFilterToken(
  input: string,
): PointFilterClassification | null {
  const trimmed = input.trim();
  if (trimmed.length === 0) {
    return null;
  }

  // Strip AutoCAD's international ("_") and transparent-command ("'")
  // prefixes so `_from` / `'from` resolve identically to `from`.
  const bare = trimmed.replace(/^['_]+/u, "");
  const upper = bare.toUpperCase();

  if (upper === "FROM") {
    return { kind: "from" };
  }

  if (upper === "M2P" || upper === "MTP") {
    return { kind: "mid" };
  }

  // Coordinate filters: a leading dot followed by 1–2 DISTINCT axis
  // letters (`.x`, `.y`, `.z`, `.xy`, `.xz`, `.yz`). A three-letter `.xyz`
  // would leave no ordinate to collect, so it is NOT a filter. A decimal
  // token such as `.5` is rejected because `5` is not an axis letter.
  if (bare.startsWith(".")) {
    const axesPart = upper.slice(1);
    if (axesPart.length >= 1 && axesPart.length <= 2) {
      const letters = axesPart.split("");
      const distinct = new Set(letters);
      if (
        distinct.size === letters.length &&
        letters.every((letter) => AXIS_LETTERS.has(letter))
      ) {
        return {
          kind: "ordinate",
          axes: {
            x: distinct.has("X"),
            y: distinct.has("Y"),
            z: distinct.has("Z"),
          },
        };
      }
    }
  }

  return null;
}

/**
 * Midpoint of two points — the resolved value of the `M2P` / `MTP`
 * ("mid between 2 points") temporary object snap.
 */
export function midBetween(
  first: FilterCoordinate,
  second: FilterCoordinate,
): FilterCoordinate {
  return {
    x: (first.x + second.x) / 2,
    y: (first.y + second.y) / 2,
    z: (first.z + second.z) / 2,
  };
}

/**
 * Combine the ordinates SUPPLIED by a coordinate filter with the remaining
 * ordinates collected from the follow-up point.
 *
 * AutoCAD `.X` example: `.X` of `supplied` → takes only `supplied.x`; then
 * "(need YZ)" of `rest` → takes `rest.y` and `rest.z`. The result is
 * `(supplied.x, rest.y, rest.z)`.
 */
export function combineOrdinateFilter(
  axes: OrdinateFilterAxes,
  supplied: FilterCoordinate,
  rest: FilterCoordinate,
): FilterCoordinate {
  return {
    x: axes.x ? supplied.x : rest.x,
    y: axes.y ? supplied.y : rest.y,
    z: axes.z ? supplied.z : rest.z,
  };
}
